/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// setTimeout(() => {
//     if (!window.theme) {
//         console.warn('theme object not found!');
//         return;
//     }
//     // Override and log
//     theme.scrollToRevealElement = (el) => {
//         console.log('scrollToRevealElement called:', el, 'scrollY:', window.scrollY);
//         // ...rest of your bulletproof code here...
//     };
//     console.log('scrollToRevealElement patched in custom.js');
// }, 2000);




//SEARCH BAR
document.addEventListener('DOMContentLoaded', function () {
    // Function to handle clicks outside the '.main-search__input'
    function handleOutsideClick(event) {
        const mainSearchInput = document.querySelector('.main-search__input');
        const showSearchLink = document.querySelector('.show-search-link');
        const showSearchLinkText = document.querySelector('.show-search-link__text');
        const mainSearchForm = document.querySelector('.main-search__form-del'); // Select the search form element

        // Check if the 'show-search' class is on the body
        if (document.body.classList.contains('show-search')) {
            // Show or hide elements based on whether 'show-search' is active
            if (showSearchLinkText) showSearchLinkText.style.display = 'none'; // Hide the element when search is active

            // Handle click outside of the search input and link
            if (mainSearchInput && !mainSearchInput.contains(event.target) && !showSearchLink.contains(event.target)) {
                document.body.classList.remove('show-search');
                if (showSearchLinkText) showSearchLinkText.style.display = ''; // Restore the element when search is not active
                mainSearchInput.value = ''; // Clear the input
                if (mainSearchForm) mainSearchForm.style.display = 'none'; // Hide the search form
            }
        } else {
            // Optionally reset visibility when search is not active
            if (showSearchLinkText) showSearchLinkText.style.display = '';
            if (mainSearchForm) mainSearchForm.style.display = ''; // Reset the display of the search form when the modal is not active
        }
    }

    // Attach the event listener to the document
    document.addEventListener('click', handleOutsideClick);

    // Additional listener for opening the search to ensure elements are hidden as needed
    const showSearchLink = document.querySelector('.show-search-link');
    if (showSearchLink) {
        showSearchLink.addEventListener('click', function () {
            const showSearchLinkText = document.querySelector('.show-search-link__text');
            const mainSearchForm = document.querySelector('.main-search__form-del'); // Select the search form element
            if (showSearchLinkText) showSearchLinkText.style.display = 'none'; // Hide when search is activated
            if (mainSearchForm) mainSearchForm.style.display = ''; // Show the search form when the search is activated
        });
    }
});

// SEARCH PAGE START
document.addEventListener('DOMContentLoaded', function () {
    // Get the current URL
    const currentUrl = window.location.href;

    // Check if the URL contains a search query or is the search page
    const isSearchPage = currentUrl.includes('/search') || currentUrl.includes('?q=');

    // Get the bc-instant-search element
    const instantSearchElement = document.querySelector('#bc-instant-search');
    const instantSearchElement2 = document.querySelector('.show-search-link__icon');
    const instantSearchElement3 = document.querySelector('.show-search-link');

    // If it's a search page, hide the search in header and the instant search 
    if (isSearchPage && instantSearchElement) {
        instantSearchElement.style.display = 'none'; // Hides the element
        instantSearchElement2.style.display = 'none'; // Hides the element
        instantSearchElement3.style.display = 'none'; // Hides the element
    }
});
// SEARCH BAR END

// VARIANT ARROW FOR MORE THAN 3 COLORS START

document.addEventListener('DOMContentLoaded', function () {
    window.toggleVisibility = function (button) {
        var parentContainer = button.closest('.product-block');
        var hiddenSwatches = parentContainer.querySelectorAll('.product-block-options__item--truncated');
        var arrow = parentContainer.querySelector('.toggle-arrow');

        if (!hiddenSwatches.length) {
            console.warn('No truncated swatches found.');
            return;
        }

        if (arrow) {
            arrow.classList.toggle('rotated');
        }

        hiddenSwatches.forEach(function (swatch) {
            swatch.classList.toggle('is-visible');
        });
    };
});

// VARIANT ARROW FOR MORE THAN 3 COLORS END


// QUICK BUY START ******************************************************************
// Common function to add to cart

document.addEventListener('on:cart:add', function () {
    updateCartCount();
});

// async function addToCart(variantId) {
//     try {
//         const response = await fetch('/cart/add.js', {
//             method: 'POST',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
//         });

//         if (!response.ok) throw new Error('Failed to add item to cart, status: ' + response.status);

//         const data = await response.json();
//         console.log('Cart updated successfully:', data);

//         document.dispatchEvent(new CustomEvent('on:cart:add', { detail: { variantId } }));
//         document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
//         document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
//         updateCartCount();

//     } catch (error) {
//         console.error('Error adding to cart:', error);
//         document.dispatchEvent(new CustomEvent('on:cart:error', { detail: { error: error.toString() } }));
//     }
// }

// // Update the cart count in the header
// function updateCartCount() {
//     fetch('/cart.js')
//         .then(response => {
//             if (!response.ok) throw new Error('Failed to fetch cart data');
//             return response.json();
//         })
//         .then(cart => {
//             const cartLinkCount = document.querySelector('.cart-link__count');
//             if (cartLinkCount) {
//                 if (cart.item_count > 0) {
//                     cartLinkCount.textContent = cart.item_count;
//                     cartLinkCount.style.display = 'inline'; // Show the count
//                 } else {
//                     cartLinkCount.style.display = 'none'; // Hide if cart is empty
//                 }
//             }
//             console.log('Cart count updated successfully');
//         })
//         .catch(error => {
//             console.error('Error fetching cart:', error);
//         });
// }


// No sizes Present
document.addEventListener('DOMContentLoaded', function () {
    // Select all elements with the 'quick-add-no-sizes' class
    const noSizeButtons = document.querySelectorAll('.quick-add-no-sizes');

    // Loop through each element and attach the click event
    noSizeButtons.forEach(button => {
        button.addEventListener('click', function (event) {
            event.preventDefault(); // Prevent any default behavior

            // Get the dynamic variant ID from the button's data attribute
            const variantId = button.getAttribute('data-variant-id');

            if (variantId) {
                // Call function to add the product to the cart with the dynamic variantId
                addToCart(variantId);
            } else {
                console.error('Variant ID not found for this product.');
            }
        });
    });
});


// If sizes are present do this
(function () {
    document.addEventListener('DOMContentLoaded', function () {
        setupObservers();
        initEventListeners();
        initColorHoverListeners(); // NEW: Initialize color hover functionality
        resetSizeSelection();
    });

    function setupObservers() {
        const targetNode = document.querySelector('.dynamic-content-area') || document.body;
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        checkAndInitProductOptions(node);
                    }
                });
            });
        });
        observer.observe(targetNode, { childList: true, subtree: true });
    }

    function checkAndInitProductOptions(node) {
        if (node.matches('.product-block-options__item') || node.querySelector('.product-block-options__item')) {
            // initProductBlockFunctions();
        }
    }

    function initEventListeners() {
        document.body.addEventListener('click', function (event) {
            const targetItem = event.target.closest('.product-block-options__item');
            if (targetItem) {
                handleOptionClick(event, targetItem);
            }
        });
    }

    // NEW: Handle color hover to set active_variant on hover
    function initColorHoverListeners() {
        document.body.addEventListener('mouseenter', function (event) {
            const colorOption = event.target.closest('.product-block-options--swatch .product-block-options__item');
            if (colorOption) {
                // Remove active_variant from sibling color options
                const siblings = colorOption.closest('.product-block-options__inner')
                    ?.querySelectorAll('.product-block-options__item');
                if (siblings) {
                    siblings.forEach(sibling => sibling.classList.remove('active_variant'));
                }
                // Add active_variant to the hovered color
                colorOption.classList.add('active_variant');
            }
        }, true); // Use capture phase to catch the event early
    }


    function handleOptionClick(e, target) {
        e.preventDefault();

        // Check if the target is a color or size option
        const productBlockOptions = target.closest('.product-block-options');
        if (!productBlockOptions) {
            console.log("No product-block-options found. Skipping.");
            return; // Safely return if target.closest() is null
        }

        const isColorOption = productBlockOptions.classList.contains('product-block-options--swatch');
        const isSizeOption = target.closest('.product-block-options__item.size');

        // Handle removing the 'active_variant' class from all sibling elements
        const productBlockOptionsInner = target.closest('.product-block-options__inner');
        if (!productBlockOptionsInner) {
            console.log("No product-block-options__inner found. Skipping.");
            return; // Safely return if the expected structure isn't found
        }

        const siblings = productBlockOptionsInner.querySelectorAll('.product-block-options__item');
        siblings.forEach(sibling => sibling.classList.remove('active_variant'));

        // Add 'active_variant' class to the clicked target
        target.classList.add('active_variant');

        // Handle color or size selection logic
        if (isColorOption) {
            return; // Update product image when a color is clicked.
        } else if (isSizeOption) {
            handleSizeSelection(target); // Handle size selection logic.
        }
    }

    function handleSizeSelection(sizeOption) {
        const blockInner = sizeOption.closest('.block-inner');
        if (!blockInner) {
            console.log('No blockInner found for size selection. Skipping this step.');
            return;
        }

        const selectedSize = sizeOption.getAttribute('data-option-item');
        const selectedColorOption = blockInner.querySelector('.product-block-options--swatch .product-block-options__item.active_variant[data-option-item]');
        const selectedColor = selectedColorOption ? selectedColorOption.getAttribute('data-option-item') : null;

        const finalVariant = SharedFunctions.getFinalVariant(blockInner, selectedSize, selectedColor);

        // Check if the size is unavailable (sold out)
        if (finalVariant && finalVariant.available) {
            SharedFunctions.addToCart(finalVariant.id);
        } else {
            console.log("Selected size is unavailable. Please choose another size.");
            // Keep "Pick a Size" button visible and display an error message
            const warningElement = sizeOption.closest('.product-block')?.querySelector('.out-of-stock-warning');
            if (warningElement) {
                warningElement.textContent = "This item is out of stock.";
                warningElement.style.display = "block"; // Make the warning visible
            }
            const pickSizeButton = document.querySelector('.pick-size-button'); // Adjust the selector based on your actual button
            if (pickSizeButton) {
                pickSizeButton.textContent = "Pick a Size (Size Unavailable)";
                pickSizeButton.classList.add('error');
            }
        }
    }

    function resetSizeSelection(blockInner = null) {
        // Check if blockInner exists before proceeding
        if (!blockInner) {
            // console.log('No blockInner found. Skipping resetSizeSelection.');
            return; // Safely return if blockInner is not defined
        }

        // Proceed only if blockInner is defined
        const sizeOptions = blockInner.querySelectorAll('.option-name-Size .product-block-options__item');
        sizeOptions.forEach(item => item.classList.remove('active_variant'));
    }
    document.addEventListener('DOMContentLoaded', function () {
        // Call resetSizeSelection only if blockInner is available (Desktop)
        const blockInner = document.querySelector('.block-inner');
        resetSizeSelection(blockInner); // Pass blockInner if available, otherwise let it use default
    });

    // Handle product with sizes
    document.addEventListener('DOMContentLoaded', function () {
        document.querySelectorAll('.del-quick-add-mbl').forEach(button => {
            button.addEventListener('click', function (event) {
                event.preventDefault();
                const productBlock = document.querySelector(`.product-block[data-product-id="${button.getAttribute('data-product-id')}"]`);
                if (!productBlock) return;

                const sizeOptions = Array.from(productBlock.querySelectorAll('.product-block-options__item.size'));

                if (sizeOptions.length === 0) {
                    const noSizeButton = document.querySelector('.quick-add-no-sizes');
                    const variantId = noSizeButton?.getAttribute('data-variant-id');
                    if (variantId) addToCart(variantId);
                } else {
                    const sizeOptionsHtml = sizeOptions.map(option => {
                        const isAvailable = !option.classList.contains('product-block-options__item--unavailable');
                        const variantId = option.getAttribute('data-variant-id');
                        return `<span class="product-block-options__item size ${isAvailable ? '' : 'product-block-options__item--unavailable'}" data-variant-id="${variantId}">
                                <span class="product-block-options__item__text">${option.textContent.trim()}</span>
                            </span>`;
                    }).join('');
                    showModal(`<div class="modalSizeContainer-del">${sizeOptionsHtml}</div>`);
                }
            });
        });

        // Modal handling
        function showModal(modalContent) {
            const fixedModalOverlay = document.getElementById('fixedSizesModalOverlay');
            const modalContainer = document.querySelector('.modalSizeContainer');

            if (!fixedModalOverlay || !modalContainer) return;

            modalContainer.innerHTML = modalContent;
            fixedModalOverlay.classList.add('show');

            document.querySelectorAll('.product-block-options__item.size').forEach(sizeOption => {
                sizeOption.addEventListener('click', function () {
                    if (!sizeOption.classList.contains('product-block-options__item--unavailable')) {
                        const variantId = sizeOption.getAttribute('data-variant-id');
                        if (variantId) {
                            addToCart(variantId);
                            closeModal();
                        }
                    } else {
                        console.log('This size is unavailable.');
                        const pickSizeButton = document.querySelector('.pick-size-button'); // Adjust selector as needed
                        if (pickSizeButton) {
                            pickSizeButton.textContent = "Pick a Size (Size Unavailable)";
                            pickSizeButton.classList.add('error');
                        }
                    }
                });
            });


            // Close modal
            document.querySelector('.close-modal')?.addEventListener('click', closeModal);
            fixedModalOverlay.addEventListener('click', event => { if (event.target === fixedModalOverlay) closeModal(); });
        }

        function closeModal() {
            const fixedModalOverlay = document.getElementById('fixedSizesModalOverlay');
            const modalContainer = document.querySelector('.modalSizeContainer');
            if (fixedModalOverlay && modalContainer) {
                fixedModalOverlay.classList.remove('show');
                modalContainer.innerHTML = '';
            }
        }
    });
})();

// SHARED
const SharedFunctions = {
    getFinalVariant(blockInner, selectedSize, selectedColor = null) {
        const selectedOptions = this.getSelectedOptions(blockInner);
        if (selectedSize) selectedOptions.push(selectedSize.toLowerCase());
        if (selectedColor) selectedOptions.unshift(selectedColor.toLowerCase()); // Add color as the first option if selected

        const productData = blockInner.getAttribute('data-product');
        if (!productData) {
            return null;
        }

        let product;
        try {
            product = JSON.parse(productData);
        } catch (error) {
            return null;
        }

        if (!product || !product.variants) {
            return null;
        }

        for (const variant of product.variants) {
            const variantOptions = [variant.option1, variant.option2, variant.option3].map(opt => opt && opt.toLowerCase());
            if (selectedOptions.every(option => variantOptions.includes(option))) {
                return variant;
            }
        }
        return null;
    },

    getSelectedOptions(blockInner) {
        return Array.from(blockInner.querySelectorAll('.product-block-options__item.active_variant'))
            .map(item => item.getAttribute('data-option-item').toLowerCase());
    },

    async addToCart(variantId) {
        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
            });

            if (!response.ok) {
                throw new Error('Failed to add item to cart, response status: ' + response.status);
            }

            const data = await response.json();
            document.dispatchEvent(new CustomEvent('on:cart:add', { detail: { variantId } }));
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        } catch (error) {
            console.error('Error adding to cart:', error);
        }
    }
};

document.addEventListener('on:cart-drawer:after-close', () => {
    // Remove 'active_variant' class from all options
    document.querySelectorAll('.product-block-options__item.active_variant').forEach((item) => {
        item.classList.remove('active_variant');
    });
});

function refreshComponent(componentSelector) {
    const component = document.querySelector(componentSelector);
    if (!component) return;

    const url = component.getAttribute('data-refresh-url'); // Ensure this attribute is set with the appropriate endpoint
    fetch(url)
        .then(response => response.json())
        .then(data => {
            component.innerHTML = data.html; // Assuming the endpoint returns new HTML for the component
        })
        .catch(error => console.error('Error refreshing component:', error));
}


// QUICK BUY END ****************************

// // QUICK BUY DEBUG START
// // No sizes Present: Handles products with no sizes
// document.addEventListener('DOMContentLoaded', function () {
//     const noSizeButtons = document.querySelectorAll('.quick-add-no-sizes');

//     noSizeButtons.forEach(button => {
//         button.addEventListener('click', function (event) {
//             event.preventDefault();

//             const variantId = button.getAttribute('data-variant-id');

//             if (variantId) {
//                 addToCart(variantId);
//             } else {
//                 console.error('Variant ID not found for this product.');
//             }
//         });
//     });

// });

// // If sizes are present, display the modal and handle size selection
// document.addEventListener('DOMContentLoaded', function () {
//     const quickBuyButtons = document.querySelectorAll('.del-quick-add-mbl');

//     quickBuyButtons.forEach(button => {
//         button.addEventListener('click', function (event) {
//             event.preventDefault();
//             const productId = button.getAttribute('data-product-id');
//             const productBlock = document.querySelector(`.product-block[data-product-id="${productId}"]`);

//             if (productBlock) {
//                 const sizeOptions = Array.from(productBlock.querySelectorAll('.product-block-options__item.size'));

//                 // If no size options, add the product to the cart directly (no size modal)
//                 if (sizeOptions.length === 0) {
//                     const noSizeButtons = document.querySelectorAll('.quick-add-no-sizes');

//                     if (noSizeButtons.length > 0) {
//                         const variantId = noSizeButtons[0].getAttribute('data-variant-id');
//                         console.log('Adding no-size product to cart with variantId:', variantId);

//                         if (variantId) {
//                             addToCart(variantId);
//                         } else {
//                             // console.error('Variant ID not found for this product.');
//                         }
//                     } else {
//                         // console.error('No elements with class "quick-add-no-sizes" found.');
//                     }

//                 } else {
//                     // If sizes exist, display the modal
//                     const sizeOptionsHtml = sizeOptions.map(option => {
//                         const isAvailable = !option.classList.contains('product-block-options__item--unavailable');
//                         const variantId = option.getAttribute('data-variant-id');

//                         if (!variantId) {
//                             console.error('Variant ID is missing for this size option:', option.outerHTML);
//                         }

//                         return `<span class="product-block-options__item size ${isAvailable ? '' : 'product-block-options__item--unavailable'}" 
//                                     data-variant-id="${variantId}">
//                                     <span class="product-block-options__item__text">${option.textContent.trim()}</span>
//                                 </span>`;
//                     }).join('');

//                     const modalContent = `<div class="modalSizeContainer-del">${sizeOptionsHtml}</div>`;
//                     showModal(modalContent);
//                 }
//             }
//         });
//     });

//     // Function to show the modal
//     function showModal(modalContent) {
//         const fixedModalOverlay = document.getElementById('fixedSizesModalOverlay');
//         const modalContainer = document.querySelector('.modalSizeContainer');

//         if (!fixedModalOverlay || !modalContainer) {
//             console.error('Modal elements not found.');
//             return;
//         }

//         modalContainer.innerHTML = modalContent;
//         fixedModalOverlay.classList.add('show');

//         // Attach event listeners to size options in the modal to add to cart
//         document.querySelectorAll('.product-block-options__item.size').forEach(sizeOption => {
//             sizeOption.addEventListener('click', function () {
//                 if (!this.classList.contains('product-block-options__item--unavailable')) {
//                     const variantId = this.getAttribute('data-variant-id');
//                     console.log('Adding product to cart with variantId:', variantId);

//                     if (variantId) {
//                         addToCart(variantId);
//                         closeModal();
//                     } else {
//                         console.error('Variant ID not found for this size.');
//                     }
//                 } else {
//                     console.log('This size is unavailable.');
//                 }
//             });
//         });

//         // Attach event listeners for closing the modal
//         const closeButton = document.querySelector('.close-modal');
//         if (closeButton) {
//             closeButton.addEventListener('click', closeModal);
//         }
//         fixedModalOverlay.addEventListener('click', function (event) {
//             if (event.target === fixedModalOverlay) {
//                 closeModal();
//             }
//         });
//     }

//     // Function to close modal
//     function closeModal() {
//         const fixedModalOverlay = document.getElementById('fixedSizesModalOverlay');
//         const modalContainer = document.querySelector('.modalSizeContainer');
//         if (fixedModalOverlay && modalContainer) {
//             fixedModalOverlay.classList.remove('show');
//             modalContainer.innerHTML = '';
//         }
//     }
// });

// async function addToCart(variantId) {
//     try {
//         const response = await fetch('/cart/add.js', {
//             method: 'POST',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify({
//                 items: [{ id: variantId, quantity: 1 }]
//             })
//         });

//         if (!response.ok) {
//             throw new Error('Failed to add item to cart, response status: ' + response.status);
//         }

//         const data = await response.json();
//         console.log('Cart updated successfully:', data);

//         // Trigger cart drawer and refresh events
//         document.dispatchEvent(new CustomEvent('on:cart:add', { detail: { variantId } }));
//         document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
//         document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

//         // Update the cart count
//         updateCartCount();

//     } catch (error) {
//         console.error('Error adding to cart:', error);
//         document.dispatchEvent(new CustomEvent('on:cart:error', { detail: { error: error.toString() } }));
//     }
// }

// function updateCartCount() {
//     fetch('/cart.js')
//         .then(response => response.json())
//         .then(cart => {
//             const cartLinkCount = document.querySelector('.cart-link__count');
//             if (cartLinkCount) {
//                 cartLinkCount.textContent = cart.item_count;
//             }
//         })
//         .catch(error => console.error('Error fetching cart:', error));
// }

// document.addEventListener('on:cart-drawer:after-close', () => {
//     // Remove 'active_variant' class from all options
//     document.querySelectorAll('.product-block-options__item.active_variant').forEach((item) => {
//         item.classList.remove('active_variant');
//     });
// });



// // QUICK BUY DEBUG END



// Tailored don't show 3xl or 4xl START
document.addEventListener('DOMContentLoaded', function () {
    const styleOptions = document.querySelectorAll('.style-swatch-del .js-option');

    styleOptions.forEach(option => {
        option.addEventListener('change', function () {
            if (this.value === 'TAILORED') {
                console.log('TAILORED selected, hiding 3XL and 4XL sizes.');
                hideSizeOption('3XL'); // Hide 3XL if "TAILORED" is selected
                hideSizeOption('4XL'); // Hide 4XL if "TAILORED" is selected
            } else {
                console.log('Non-TAILORED selected, showing 3XL and 4XL sizes.');
                showSizeOption('3XL'); // Show 3XL for other styles
                showSizeOption('4XL'); // Show 4XL for other styles
            }
        });
    });

    // Function to hide the size options
    function hideSizeOption(sizeValue) {
        // Select the input with value sizeValue and find the corresponding label
        const sizeOption = document.querySelector(`.size-swatch-del .js-option[value="${sizeValue}"]`);
        console.log('Size option to hide:', sizeOption);

        if (sizeOption) {
            // Find the corresponding label for the size input
            const label = document.querySelector(`label[for="${sizeOption.id}"]`);
            console.log('Label to hide for size option:', label);

            if (label) {
                console.log(`Hiding size ${sizeValue}`);
                label.style.display = 'none'; // Hide the label (and effectively the size option)
            } else {
                console.log('Label not found for size option.');
            }
        } else {
            console.log(`Size option ${sizeValue} not found.`);
        }
    }

    // Tailored don't show 3xl or 4xl END

    // Function to show the size options
    function showSizeOption(sizeValue) {
        // Select the input with value sizeValue and find the corresponding label
        const sizeOption = document.querySelector(`.size-swatch-del .js-option[value="${sizeValue}"]`);
        console.log('Size option to show:', sizeOption);

        if (sizeOption) {
            // Find the corresponding label for the size input
            const label = document.querySelector(`label[for="${sizeOption.id}"]`);
            console.log('Label to show for size option:', label);

            if (label) {
                console.log(`Showing size ${sizeValue}`);
                label.style.display = 'flex'; // Show the label (and the size option)
            } else {
                console.log('Label not found for size option.');
            }
        } else {
            console.log(`Size option ${sizeValue} not found.`);
        }
    }
});


// Delman Collection Filter add out of stock to size. START

document.addEventListener('change', function (e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;

    // Only react to changes inside the Size filter group
    const sizeGroup = input.closest('.size-swatch-del-filter');
    if (!sizeGroup) return;

    // Prefer adding a hidden input to the filter form (works with autosubmit UIs)
    const form =
        input.closest('form') ||
        document.querySelector('form[action*="/collections"]') ||
        document.querySelector('form'); // fallback

    if (form) {
        const NAME = 'filter.v.availability';
        let hidden = form.querySelector(`input[name="${NAME}"]`);
        if (!hidden) {
            hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = NAME;
            form.appendChild(hidden);
        }
        hidden.value = '1';

        // If your theme auto-submits on change, we're done.
        // If it doesn't, uncomment the next line to force submit:
        // form.requestSubmit ? form.requestSubmit() : form.submit();
        return;
    }

    // Fallback: directly update the URL (preserves other query params)
    const url = new URL(window.location.href);
    if (url.searchParams.get('filter.v.availability') !== '1') {
        url.searchParams.set('filter.v.availability', '1');
        window.location.assign(url.toString());
    }
});

// Delman Collection Filter add out of stock to size. END

// Tailored don't show 3xl END

// COLLECTION PAGE FILTERS START
// document.addEventListener('DOMContentLoaded', function () {
//     var toggleButton = document.querySelector('[data-toggle-filters]');
//     if (toggleButton) {
//         toggleButton.addEventListener('click', function (event) {
//             event.preventDefault(); // Prevent default link behavior
//             var textSpan = toggleButton.querySelector('.toggle-btn__text');
//             if (textSpan.textContent.includes('Show')) {
//                 textSpan.textContent = 'HIDE FILTER';
//             } else {
//                 textSpan.textContent = 'FILTER';
//             }
//             // Add logic here if you need to actually toggle filters visibility
//         });
//     }
// });
// COLLECTION PAGE FILTERS END


// Logo shrink on scroll
(function () {
    let _scrollHandler = null;
    let _resizeHandler = null;

    function initMiniLogoScroll() {
        // Clean up previous listeners
        if (_scrollHandler) window.removeEventListener('scroll', _scrollHandler);
        if (_resizeHandler) window.removeEventListener('resize', _resizeHandler);
        _scrollHandler = null;
        _resizeHandler = null;

        const pageheader = document.querySelector('.pageheader');
        if (!pageheader) return;

        // Only run when the mini logo feature is active (del-main-logo class exists)
        if (!document.querySelector('.del-main-logo')) return;

        // If "always mini" is enabled, lock to mini state and skip scroll listener
        if (pageheader.dataset.miniLogoAlways === 'true') {
            pageheader.classList.add('is-mini-logo', 'shrunk');
            pageheader.classList.remove('is-main-logo');
            return;
        }

        const threshold = 110;
        let ticking = false;

        function setHeaderState() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            if (scrollTop > threshold) {
                pageheader.classList.add('is-mini-logo', 'shrunk');
                pageheader.classList.remove('is-main-logo');
            } else {
                pageheader.classList.add('is-main-logo');
                pageheader.classList.remove('is-mini-logo', 'shrunk');
            }
            ticking = false;
        }

        _scrollHandler = function () {
            if (!ticking) {
                window.requestAnimationFrame(setHeaderState);
                ticking = true;
            }
        };
        _resizeHandler = setHeaderState;

        setHeaderState();
        window.addEventListener('scroll', _scrollHandler, { passive: true });
        window.addEventListener('resize', _resizeHandler);
    }

    document.addEventListener('DOMContentLoaded', initMiniLogoScroll);

    // Re-initialize when the theme editor re-renders the header section
    document.addEventListener('shopify:section:load', function (event) {
        if (event.target && event.target.querySelector('.pageheader')) {
            initMiniLogoScroll();
        }
    });
})();

// Split nav: wire right proxy nav (#proxy-nav-right) to main-navigation dropdowns.
// The theme's own proxy system handles #proxy-nav (left half) automatically via
// data-proxy-nav="proxy-nav". We only need to bridge the right half here.
(function () {
    function initSplitNav() {
        var rightProxy = document.getElementById('proxy-nav-right');
        if (!rightProxy) return;

        var mainNavEl = document.querySelector('main-navigation');
        if (!mainNavEl) return;

        var leftProxy = document.getElementById('proxy-nav');
        var leftCount = leftProxy
            ? leftProxy.querySelectorAll('.navigation__tier-1 > .navigation__item').length
            : 0;

        var mainNavItems = mainNavEl.querySelectorAll(
            '.navigation.navigation--main .navigation__tier-1 > .navigation__item'
        );

        // Per-item: forward mouseenter/mouseleave so dropdowns open/close
        rightProxy.querySelectorAll('.navigation__tier-1 > .navigation__item').forEach(function (proxyItem, i) {
            var mainItem = mainNavItems[leftCount + i];
            if (!mainItem) return;
            proxyItem.addEventListener('mouseenter', function () {
                mainItem.dispatchEvent(new Event('mouseenter'));
            });
            proxyItem.addEventListener('mouseleave', function () {
                mainItem.dispatchEvent(new Event('mouseleave'));
            });
        });

        // Container-level: forward mouseenter/mouseleave to main-navigation so
        // the transparent-header class (section-header--nav-hover) toggles correctly
        rightProxy.addEventListener('mouseenter', function () {
            mainNavEl.dispatchEvent(new Event('mouseenter'));
        });
        rightProxy.addEventListener('mouseleave', function () {
            mainNavEl.dispatchEvent(new Event('mouseleave'));
        });
    }

    document.addEventListener('DOMContentLoaded', initSplitNav);
    document.addEventListener('shopify:section:load', function (event) {
        if (event.target && event.target.querySelector('.pageheader')) {
            initSplitNav();
        }
    });
})();

// document.addEventListener('DOMContentLoaded', function () {
//     const pageheader = document.querySelector('.pageheader');
//     const threshold = 110;
//     if (!pageheader) return;

//     function setHeaderState() {
//         if (window.scrollY > threshold) {
//             pageheader.classList.add('is-mini-logo', 'shrunk');
//             pageheader.classList.remove('is-main-logo');
//         } else {
//             pageheader.classList.add('is-main-logo');
//             pageheader.classList.remove('is-mini-logo', 'shrunk');
//         }
//     }

//     setHeaderState();
//     window.addEventListener('scroll', setHeaderState);
//     window.addEventListener('resize', setHeaderState);
// });



// // PICK A SIZE ORIGINAL with bad setAddToCartText***
// document.addEventListener('DOMContentLoaded', function () {
//     const parentContainer = document.querySelector('.size-swatch-del');
//     let sizeOptions = parentContainer?.querySelectorAll('input[type="radio"].opt-btn') || [];
//     const addToCartButton = document.querySelector('.add-to-cart');
//     const disclaimer = document.getElementById('pick-a-size-disclaimer');
//     const variantImagesContainer = document.querySelector('.variant-images');
//     const addToCartText = "Add to Cart";
//     const pickSizeText = "Pick a Size";
//     let isSizeSelectedManually = false; // Tracks manual selection

//     // Function to update variant images based on the selected variant
//     function updateVariantImages(variant) {
//         if (variant && variant.featured_image) {
//             const newImageSrc = variant.featured_image.src;
//             if (variantImagesContainer) {
//                 const imgTag = variantImagesContainer.querySelector('img');
//                 if (imgTag) {
//                     imgTag.src = newImageSrc;
//                 }
//             }
//         }
//     }
//     if (sizeOptions.length === 0) {
//         // If there are no size options, set button to "Add to Cart" and return early
//         setAddToCartButtonText(addToCartText);
//         return;
//     } else {
//         // Function to handle size option click
//         function handleSizeOptionClick(event) {
//             isSizeSelectedManually = true; // Mark size as manually selected
//             parentContainer.classList.remove('no-border-box-shadow');

//             // Update button to "Add to Cart" when a size is manually selected
//             setAddToCartButtonText(addToCartText);

//             // Hide disclaimer when a size is selected
//             if (disclaimer) disclaimer.style.display = 'none';

//             // Manually set the variant to match the selected size
//             const selectedSize = event.target.value;
//             updateVariantInURL(selectedSize);
//         }

//         // Function to programmatically select the first available size but keep "Pick a Size" on the button
//         function selectFirstAvailableSize() {
//             let sizeSelected = false;

//             sizeOptions.forEach(option => {
//                 const isUnavailable = option.classList.contains('is-unavailable');
//                 const isSoldOut = option.getAttribute('title') === 'Sold out';

//                 if (!sizeSelected && !option.disabled && !isUnavailable && !isSoldOut) {
//                     option.checked = true; // Programmatically select the option
//                     sizeSelected = true;
//                     parentContainer.classList.add('no-border-box-shadow');
//                     updateVariantInURL(option.value); // Update variant to reflect the selected size
//                 } else {
//                     option.checked = false; // Ensure others are not checked
//                 }
//             });

//             // Keep button as "Pick a Size" until user clicks a size manually
//             if (!isSizeSelectedManually) {
//                 setAddToCartButtonText(pickSizeText);
//                 if (disclaimer) disclaimer.style.display = 'none';
//             }
//         }

//         // Function to update the URL to reflect the selected variant
//         function updateVariantInURL(sizeValue) {
//             const variantInput = document.querySelector(`input[data-size='${sizeValue}']`);
//             if (variantInput) {
//                 variantInput.click(); // Programmatically trigger the variant input click
//             }
//         }

//         // Function to attach event listeners to size options
//         function attachSizeOptionListeners() {
//             sizeOptions.forEach(option => {
//                 option.removeEventListener('click', handleSizeOptionClick);
//             });

//             sizeOptions = parentContainer?.querySelectorAll('input[type="radio"].opt-btn') || [];
//             sizeOptions.forEach(option => {
//                 option.addEventListener('click', handleSizeOptionClick);
//             });
//         }

//         // Handle Add to Cart button click
//         addToCartButton.addEventListener('click', function (event) {
//             if (addToCartButton.getAttribute('data-add-to-cart-text') === pickSizeText) {
//                 event.preventDefault(); // Prevent adding to cart if no size is selected

//                 if (disclaimer) {
//                     disclaimer.style.display = 'block';
//                 }
//             }
//         });

//         // Set the button text and attribute in a consistent way
//         function setAddToCartButtonText(text) {
//             addToCartButton.setAttribute('data-add-to-cart-text', text);
//             addToCartButton.textContent = text;
//         }


//         // Initial state
//         setAddToCartButtonText(pickSizeText);
//         sizeOptions.forEach(option => {
//             option.checked = false;
//         });
//         if (disclaimer) disclaimer.style.display = 'none';
//         selectFirstAvailableSize();
//         attachSizeOptionListeners();

//         // MutationObserver to enforce button text
//         const observer = new MutationObserver(() => {
//             if (!isSizeSelectedManually && addToCartButton.textContent !== pickSizeText) {
//                 setAddToCartButtonText(pickSizeText);
//             }
//         });

//         observer.observe(addToCartButton, { childList: true, attributes: true });

//         // Listen for variant changes
//         document.addEventListener('on:variant:change', function (event) {
//             const { variant } = event.detail;
//             handleColorOrStyleSelection(variant);

//         });

//         // Handle changes in color/style swatch selection to ensure the state is correct
//         document.addEventListener('change', function (event) {
//             if (event.target.matches('.color-swatch-del input, .style-swatch-del input')) {
//                 const variant = event.target.dataset.variant;

//                 // If a size has been manually selected, keep "Add to Cart" state
//                 if (isSizeSelected()) {
//                     setAddToCartButtonText(addToCartText);
//                 } else {
//                     // Reset the manual selection state and set button to "Pick a Size"
//                     isSizeSelectedManually = false;
//                     setAddToCartButtonText(pickSizeText);
//                 }

//                 handleColorOrStyleSelection(variant);
//             }
//         });

//         // Helper function to determine if a size is selected
//         function isSizeSelected() {
//             return document.querySelector('input[type="radio"].opt-btn:checked') !== null;
//         }
//     }
//     // Custom function to initialize Klaviyo BIS
//     // Wait until the Klaviyo script has fully loaded
//     // Function to initialize Klaviyo BIS
//     function initializeKlaviyoBIS() {
//         klaviyo.init({
//             account: "qFwiMm",
//             platform: "shopify",
//             exclude_on_tags: "back_in_stock_hidden",
//             list: 'mK5TrB'
//         });
//         klaviyo.enable("backinstock", {
//             trigger: {
//                 product_page_text: "JOIN THE WAITLIST",
//                 product_page_class: "button btn btn--large notify_me_btn",
//                 product_page_text_align: "center",
//                 product_page_margin: "0px",
//                 alternate_anchor: "AddToCart",
//                 replace_anchor: true
//             },
//             modal: {
//                 headline: "{product_name}",
//                 body_content: "Register to receive a notification when this item comes back in stock.",
//                 email_field_label: "Email",
//                 button_label: "Notify me when available",
//                 subscription_success_label: "You're in! We'll let you know when it's back."
//             }
//         });

//         console.log("Klaviyo BIS initialized");
//     }

//     // Function to check if a size is selected
//     function isSizeSelected() {
//         return document.querySelector('input[type="radio"].opt-btn:checked') !== null;
//     }

//     // Trigger Klaviyo BIS only after a size has been selected
//     document.addEventListener('change', function (event) {
//         if (event.target.matches('.size-swatch-del input')) {
//             // Check if a size has been selected
//             if (isSizeSelected()) {
//                 console.log("Size selected, initializing Klaviyo BIS");
//                 initializeKlaviyoBIS();
//                 triggerKlaviyoBIS();
//             }
//         }
//     });

//     // Function to show the Klaviyo BIS button if initialized
//     function triggerKlaviyoBIS() {
//         const waitlistButton = document.querySelector('.klaviyo-waitlist-button');
//         if (waitlistButton) {
//             waitlistButton.style.display = 'block'; // Show the BIS button
//             console.log("Klaviyo BIS button shown");
//         } else {
//             console.log("Klaviyo BIS button not found");
//         }
//     }


// });


// // PICK A SIZE fixed START ****
function hookPickASize(root) {
    if (!root) return;
    const PICK = 'Pick a Size';
    const ADD = 'Add to Cart';
    let manual = false;

    const swatches = Array.from(root.querySelectorAll('input.opt-btn'));
    const btn = document.querySelector('.add-to-cart');
    const disc = document.getElementById('pick-a-size-disclaimer');

    function getLabelEl(input, root) {
        let label = null;
        if (input.parentElement?.classList.contains('opt-label')) label = input.parentElement;
        else if (input.nextElementSibling?.classList.contains('opt-label')) label = input.nextElementSibling;
        // Only match if parent .size-swatch-del is THIS root
        if (label && label.closest('.size-swatch-del') === root) return label;
        return null;
    }

    function setText(txt) {
        if (!btn) return;
        btn.dataset.addToCartText = txt;
        btn.textContent = txt;
    }

    function reset() {
        manual = false;
        setText(swatches.length ? PICK : ADD);
        if (disc) disc.style.display = 'none';
        swatches.forEach(i => getLabelEl(i, root)?.classList.remove('user-selected'));
    }

    swatches.forEach(input => {
        input.addEventListener('click', () => {
            manual = true;
            setText(ADD);
            if (disc) disc.style.display = 'none';
            swatches.forEach(i => getLabelEl(i, root)?.classList.remove('user-selected'));
            getLabelEl(input, root)?.classList.add('user-selected');
            // (Optional: sync variant input here)
        });
    });

    if (btn) {
        btn.addEventListener('click', e => {
            if (swatches.length && !manual) {
                e.preventDefault();
                if (disc) disc.style.display = 'block';
                return;
            }
            setTimeout(reset, 300);
        });
    }

    reset();
}


// ——— Run pick-a-size on both desktop & mobile containers —————————

function initPickASize() {
    hookPickASize(document.querySelector('.size-swatch-del'));
    hookPickASize(document.querySelector('.modalSizeContainer'));
}

// 1) On page load
document.addEventListener('DOMContentLoaded', initPickASize);

// 2) If your theme fires a quick-add event
document.body.addEventListener('quick-add:open', initPickASize);

// 3) When your mobile “QUICK ADD” button is clicked
document.body.addEventListener('click', e => {
    if (e.target.matches('.del-quick-add-mbl')) {
        // wait for custom.js to inject swatches
        setTimeout(initPickASize, 100);
    }
});

// 4) Watch for swatches being loaded into the mobile modal
const mob = document.querySelector('.modalSizeContainer');
if (mob) {
    new MutationObserver(() => {
        if (mob.querySelector('input.opt-btn')) {
            console.log('[PickASize] mobile swatches loaded');
            initPickASize();
        }
    }).observe(mob, { childList: true, subtree: true });
}
// Intercept mobile quick-add for single-variant products
document.body.addEventListener('click', function (e) {
    const btn = e.target.closest('.del-quick-add-mbl');
    if (!btn) return;

    // Parse the full variants array from the button
    let variants = [];
    try {
        variants = JSON.parse(btn.getAttribute('data-variants') || '[]');
    } catch (_) { }

    // If there's exactly one variant, handle it ourselves
    if (variants.length === 1) {
        e.preventDefault();  // stop the theme’s default handler

        const vid = variants[0].id;
        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: vid, quantity: 1 })
        })
            .then(res => res.json())
            .then(item => {
                console.log(`[Mobile QuickAdd] Added variant ${vid}`, item);
                // trigger your reset & cart-updated flows:
                document.body.dispatchEvent(new Event('cart:updated'));
            })
            .catch(err => {
                console.error('[Mobile QuickAdd] error adding to cart', err);
            });
    }
});
// // PICK A SIZE fixed END ****
// CUSTOM ERROR MESSAGE - START
document.addEventListener('on:cart:error', (event) => {
    // Your code to handle the cart error event

    // Assuming the event contains the error details, you can access them here
    const error = event.detail; // Check what error details are provided in the event

    // Find the error div where you want to display the custom error message
    const errorDiv = document.querySelector('.js-form-error');

    if (errorDiv) {
        // Customize the error message based on the event details
        const customErrorMessage = "*The quantity you selected exceeds available stock."; // Your custom message

        // Set the error message in the error div
        errorDiv.textContent = customErrorMessage;
        errorDiv.removeAttribute('hidden'); // Ensure the error message is visible

        // Optionally log the error details for debugging
        console.log('Cart error event:', error);
    }
});

// CUSTOM ERROR MESSAGE - END

// Rebuy
document.addEventListener('rebuy:cart.change', (event) => {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    updateCartCount();

});

// Gift Card Carty Drawer START
document.addEventListener('change', (event) => {
    // Check if the event is triggered by the gift box checkbox
    const giftBoxCheckbox = event.target.closest('#gift-box');

    if (giftBoxCheckbox && giftBoxCheckbox.checked) {
        // Static variant ID for the Gift Box product
        const giftBoxVariantId = '40122118275156'; // Replace with your actual Gift Box variant ID

        // Add the Gift Box product to the cart
        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: giftBoxVariantId,
                quantity: 1,
                properties: { "Gift Box": "Yes" } // Include properties if required
            }),
        })
            .then((response) => {
                if (!response.ok) {
                    return response.json().then((errorData) => {
                        throw new Error(errorData.message || 'Failed to add the Gift Box to the cart');
                    });
                }
                return response.json();
            })
            .then((data) => {
                // Refresh cart drawer or notify the user
                document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
                // Hide the gift box container after adding to the cart
                const giftBoxContainer = document.querySelector('.gift-box-container');
                if (giftBoxContainer) {
                    giftBoxContainer.style.display = 'none';
                }
            })
            .catch((error) => {
                console.error('Error adding Gift Box:', error);
            });
    }
});

// Gift Card Carty Drawer END

// Hide Size Diclosure if size chart is not there 

document.addEventListener('DOMContentLoaded', function () {
    // Select the button using its class
    var sizeChartButton = document.querySelector('.size-chart-link');

    // Select the element to hide
    var sizeDisclosure = document.querySelector('.size-disclosure-del');

    // Check if the button does NOT exist
    if (!sizeChartButton) {
        // If the button does not exist, hide the size disclosure element
        if (sizeDisclosure) {
            sizeDisclosure.style.display = 'none';
        }
    }
});

// Working on Collection Filter first selected scwatch

// document.addEventListener('DOMContentLoaded', function () {
//     console.log('Document loaded. Adding active_variant to first available color options.');

//     // Function to add active_variant to the first available color option
//     function applyActiveVariantToProducts() {
//         const productBlocks = document.querySelectorAll('.block-inner');
//         console.log(`Found ${productBlocks.length} product blocks.`);

//         productBlocks.forEach((blockInner, index) => {
//             console.log(`Processing block #${index + 1}:`, blockInner);

//             // Find all color options in this block
//             const colorOptions = blockInner.querySelectorAll('.product-block-options--swatch .product-block-options__item');

//             if (colorOptions.length > 0) {
//                 console.log(`Block #${index + 1}: Found color options`, colorOptions);

//                 // Loop through the options and find the first available one
//                 for (const colorOption of colorOptions) {
//                     if (!colorOption.classList.contains('product-block-options__item--unavailable')) {
//                         colorOption.classList.add('active_variant'); // Add active_variant class
//                         console.log(`Block #${index + 1}: Added active_variant to`, colorOption);
//                         break; // Only add active_variant to the first available option
//                     }
//                 }
//             } else {
//                 console.log(`Block #${index + 1}: No color options found.`);
//             }
//         });
//     }

//     // Initial application
//     applyActiveVariantToProducts();

//     // Use MutationObserver to handle dynamically added product blocks
//     const observer = new MutationObserver(() => {
//         console.log('Detected DOM changes. Reapplying active_variant.');
//         applyActiveVariantToProducts();
//     });

//     // Observe the container that dynamically loads products
//     const ajaxContainer = document.querySelector('[data-ajax-container]');
//     if (ajaxContainer) {
//         observer.observe(ajaxContainer, { childList: true, subtree: true });
//         console.log('MutationObserver is set up on the Ajax container.');
//     } else {
//         console.warn('No Ajax container found for MutationObserver.');
//     }
// });

// // Delman Image Edge Detection for 3/4 START
// function checkImageEdges(imgElement) {
//     const imageUrl = imgElement.src;
//     // console.log('=== CHECKING IMAGE ===');
//     // console.log('URL:', imageUrl);

//     // Create a fresh image to check
//     const testImg = new Image();
//     testImg.crossOrigin = 'anonymous';

//     testImg.onload = function () {
//         try {
//             const canvas = document.createElement('canvas');
//             const ctx = canvas.getContext('2d');
//             canvas.width = testImg.width;
//             canvas.height = testImg.height;

//             // Calculate aspect ratio
//             const aspectRatio = canvas.width / canvas.height;
//             // console.log('Aspect ratio:', aspectRatio.toFixed(3));

//             // If already 3:4 (0.75), skip detection
//             if (Math.abs(aspectRatio - 0.75) < 0.01) {
//                 // console.log('✓ Already 3:4 ratio - skipping detection');
//                 return;
//             }

//             ctx.drawImage(testImg, 0, 0);

//             const whiteThreshold = 240;
//             let leftWhiteCount = 0;
//             let rightWhiteCount = 0;
//             const totalRows = canvas.height;

//             for (let y = 0; y < canvas.height; y++) {
//                 const leftPixel = ctx.getImageData(0, y, 1, 1).data;
//                 if (leftPixel[0] >= whiteThreshold && leftPixel[1] >= whiteThreshold && leftPixel[2] >= whiteThreshold) {
//                     leftWhiteCount++;
//                 }

//                 const rightPixel = ctx.getImageData(canvas.width - 1, y, 1, 1).data;
//                 if (rightPixel[0] >= whiteThreshold && rightPixel[1] >= whiteThreshold && rightPixel[2] >= whiteThreshold) {
//                     rightWhiteCount++;
//                 }
//             }

//             const leftWhitePercentage = (leftWhiteCount / totalRows) * 100;
//             const rightWhitePercentage = (rightWhiteCount / totalRows) * 100;

//             // console.log('Left:', leftWhitePercentage.toFixed(1) + '% | Right:', rightWhitePercentage.toFixed(1) + '%');

//             const imgArContainer = imgElement.parentElement;

//             if (leftWhitePercentage > 80 && rightWhitePercentage > 80) {
//                 // console.log('✓ WHITE EDGES - no-detected-edge');
//                 if (imgArContainer) {
//                     imgArContainer.classList.remove('detected-edge');
//                     imgArContainer.classList.add('no-detected-edge');
//                 }
//             } else {
//                 // console.log('✓ FILLED EDGES - detected-edge');
//                 if (imgArContainer) {
//                     imgArContainer.classList.remove('no-detected-edge');
//                     imgArContainer.classList.add('detected-edge');
//                 }
//             }

//         } catch (error) {
//             console.error('Error:', error);
//         }
//     };

//     testImg.onerror = function () {
//         console.error('Failed to load image');
//     };

//     testImg.src = imageUrl;
// }

// function processWhenLoaded(img) {
//     if (img.dataset.edgeProcessed === 'done') return;
//     img.dataset.edgeProcessed = 'done';

//     if (img.complete && img.naturalWidth > 0) {
//         checkImageEdges(img);
//     } else {
//         img.addEventListener('load', () => checkImageEdges(img), { once: true });
//     }
// }

// const observer = new IntersectionObserver((entries) => {
//     entries.forEach(entry => {
//         if (entry.isIntersecting) {
//             processWhenLoaded(entry.target);
//         }
//     });
// }, { rootMargin: '100px' });

// document.querySelectorAll('.product-block__image img, body.template-product .product-media--image img').forEach(img => {
//     observer.observe(img);
//     if (img.complete) processWhenLoaded(img);
// });

// // console.log('✓ Edge detection initialized');
// // Delman Image Edge Detection for 3/4 END

// // Delman Image Edge Detection for Product Page START
// function checkProductImageEdges(imgElement) {
//     const imageUrl = imgElement.src;

//     const testImg = new Image();
//     testImg.crossOrigin = 'anonymous';

//     testImg.onload = function () {
//         try {
//             const canvas = document.createElement('canvas');
//             const ctx = canvas.getContext('2d');
//             canvas.width = testImg.width;
//             canvas.height = testImg.height;

//             const aspectRatio = canvas.width / canvas.height;

//             if (Math.abs(aspectRatio - 0.75) < 0.01) {
//                 return;
//             }

//             ctx.drawImage(testImg, 0, 0);

//             const whiteThreshold = 240;
//             let leftWhiteCount = 0;
//             let rightWhiteCount = 0;
//             const totalRows = canvas.height;

//             for (let y = 0; y < canvas.height; y++) {
//                 const leftPixel = ctx.getImageData(0, y, 1, 1).data;
//                 if (leftPixel[0] >= whiteThreshold && leftPixel[1] >= whiteThreshold && leftPixel[2] >= whiteThreshold) {
//                     leftWhiteCount++;
//                 }

//                 const rightPixel = ctx.getImageData(canvas.width - 1, y, 1, 1).data;
//                 if (rightPixel[0] >= whiteThreshold && rightPixel[1] >= whiteThreshold && rightPixel[2] >= whiteThreshold) {
//                     rightWhiteCount++;
//                 }
//             }

//             const leftWhitePercentage = (leftWhiteCount / totalRows) * 100;
//             const rightWhitePercentage = (rightWhiteCount / totalRows) * 100;

//             const imgArContainer = imgElement.parentElement;

//             if (leftWhitePercentage > 80 && rightWhitePercentage > 80) {
//                 if (imgArContainer) {
//                     imgArContainer.classList.remove('detected-edge');
//                     imgArContainer.classList.add('no-detected-edge');
//                 }
//             } else {
//                 if (imgArContainer) {
//                     imgArContainer.classList.remove('no-detected-edge');
//                     imgArContainer.classList.add('detected-edge');
//                 }
//             }

//         } catch (error) {
//             console.error('Error:', error);
//         }
//     };

//     testImg.onerror = function () {
//         console.error('Failed to load image');
//     };

//     testImg.src = imageUrl;
// }

// function processProductWhenLoaded(img) {
//     if (img.dataset.productEdgeProcessed === 'done') return;
//     img.dataset.productEdgeProcessed = 'done';

//     if (img.complete && img.naturalWidth > 0) {
//         checkProductImageEdges(img);
//     } else {
//         img.addEventListener('load', () => checkProductImageEdges(img), { once: true });
//     }
// }

// const productObserver = new IntersectionObserver((entries) => {
//     entries.forEach(entry => {
//         if (entry.isIntersecting) {
//             processProductWhenLoaded(entry.target);
//         }
//     });
// }, { rootMargin: '300px' });

// document.querySelectorAll('.del-multiply-bg img').forEach(img => {
//     productObserver.observe(img);
//     if (img.complete) processProductWhenLoaded(img);
// });
// // Delman Image Edge Detection for Product Page END
// // Delman Image Edge Detection for Complementary Products Carousel START
// function checkCarouselImageEdges(imgElement) {
//     const imageUrl = imgElement.src;

//     const testImg = new Image();
//     testImg.crossOrigin = 'anonymous';

//     testImg.onload = function () {
//         try {
//             const canvas = document.createElement('canvas');
//             const ctx = canvas.getContext('2d');
//             canvas.width = testImg.width;
//             canvas.height = testImg.height;

//             const aspectRatio = canvas.width / canvas.height;

//             if (Math.abs(aspectRatio - 0.75) < 0.01) {
//                 return;
//             }

//             ctx.drawImage(testImg, 0, 0);

//             const whiteThreshold = 240;
//             let leftWhiteCount = 0;
//             let rightWhiteCount = 0;
//             const totalRows = canvas.height;

//             for (let y = 0; y < canvas.height; y++) {
//                 const leftPixel = ctx.getImageData(0, y, 1, 1).data;
//                 if (leftPixel[0] >= whiteThreshold && leftPixel[1] >= whiteThreshold && leftPixel[2] >= whiteThreshold) {
//                     leftWhiteCount++;
//                 }

//                 const rightPixel = ctx.getImageData(canvas.width - 1, y, 1, 1).data;
//                 if (rightPixel[0] >= whiteThreshold && rightPixel[1] >= whiteThreshold && rightPixel[2] >= whiteThreshold) {
//                     rightWhiteCount++;
//                 }
//             }

//             const leftWhitePercentage = (leftWhiteCount / totalRows) * 100;
//             const rightWhitePercentage = (rightWhiteCount / totalRows) * 100;

//             const imgArContainer = imgElement.parentElement;

//             if (leftWhitePercentage > 80 && rightWhitePercentage > 80) {
//                 if (imgArContainer) {
//                     imgArContainer.classList.remove('detected-edge');
//                     imgArContainer.classList.add('no-detected-edge');
//                 }
//             } else {
//                 if (imgArContainer) {
//                     imgArContainer.classList.remove('no-detected-edge');
//                     imgArContainer.classList.add('detected-edge');
//                 }
//             }

//         } catch (error) {
//             console.error('Carousel edge detection error:', error);
//         }
//     };

//     testImg.onerror = function () {
//         console.error('Failed to load carousel image');
//     };

//     testImg.src = imageUrl;
// }

// function processCarouselWhenLoaded(img) {
//     if (img.dataset.carouselEdgeProcessed === 'done') return;
//     img.dataset.carouselEdgeProcessed = 'done';

//     if (img.complete && img.naturalWidth > 0) {
//         checkCarouselImageEdges(img);
//     } else {
//         img.addEventListener('load', () => checkCarouselImageEdges(img), { once: true });
//     }
// }

// function scanForCarouselImages() {
//     document.querySelectorAll('body.template-product div.product-block__image.product-block__image--primary div.del-multiply-bg img').forEach(img => {
//         processCarouselWhenLoaded(img);
//     });
// }

// // Initial scan
// scanForCarouselImages();

// // Delayed scans for lazy-loaded content
// setTimeout(scanForCarouselImages, 500);
// setTimeout(scanForCarouselImages, 1000);
// setTimeout(scanForCarouselImages, 2000);

// // Watch for new images being added to the DOM
// const carouselMutationObserver = new MutationObserver(() => {
//     scanForCarouselImages();
// });

// if (document.body.classList.contains('template-product')) {
//     carouselMutationObserver.observe(document.body, {
//         childList: true,
//         subtree: true
//     });
// }

// Only process images without pre-computed results
document.querySelectorAll('.needs-edge-detection img').forEach(img => {
    observer.observe(img);
});
// // Delman Image Edge Detection for Complementary Products Carousel END